import { Icon, LatLng, Marker as MarkerC } from "leaflet";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, Marker, TileLayer, useMap } from "react-leaflet";

import "leaflet/dist/leaflet.css";
import Mapmarker from "/map-marker.png";

import { CopyOnHover } from "@/components/ui/copy-button";
import { cn } from "@/lib/utils";

type TProps = {
  height: string;
  width: string;
  value?: {
    lat?: number;
    lng?: number;
  };
  onChange: (data: LatLng, displayName?: string) => void;
  copyLatClassName?: string;
};

export const UpdateCenter = ({ center }: { center: LatLng }): null => {
  const map = useMap();
  map.flyTo(center);

  return null;
};

const MapPicker: React.FC<TProps> = ({
  height,
  width,
  onChange,
  value,
  copyLatClassName,
}) => {
  const [position, setPosition] = useState({
    lat: value?.lat || -33.8796248,
    lng: value?.lng || 151.2067721,
  });

  const markerRef = useRef<MarkerC>(null);

  const mapMarker = new Icon({
    iconUrl: Mapmarker,
    iconSize: [25, 40], // size of the icon
  });

  const fetchDisplayName = useCallback(
    async (lat: number, lng: number): Promise<string> => {
      // Ensure Google Maps JavaScript API is loaded
      if (!window.google || !window.google.maps) {
        return "";
      }

      const geocoder = new window.google.maps.Geocoder();
      const latLng = new window.google.maps.LatLng(lat, lng);

      try {
        const results = await new Promise<google.maps.GeocoderResult[]>(
          (resolve, reject) => {
            geocoder
              .geocode({ location: latLng }, (results, status) => {
                if (status === "OK" && results) {
                  resolve(results);
                } else {
                  reject(new Error("Geocode failed: " + status));
                }
              })
              .catch((error) => {
                reject(error);
              });
          },
        );

        return results[0]?.formatted_address || "";
      } catch (error) {
        return "";
      }
    },
    [],
  );

  const eventHandlers = useMemo(
    () => ({
      dragend: async (): Promise<void> => {
        const marker = markerRef.current;

        if (marker != null) {
          setPosition(marker.getLatLng());
          const displayName = await fetchDisplayName(
            marker.getLatLng().lat,
            marker.getLatLng().lng,
          );
          onChange(marker.getLatLng(), displayName);
        }
      },
    }),
    [onChange, fetchDisplayName],
  );

  useEffect(() => {
    setPosition({
      lat: value?.lat || -33.8796248,
      lng: value?.lng || 151.2067721,
    });
  }, [value]);

  const latLngPosition = new LatLng(position.lat, position.lng);

  return (
    <div
      style={{
        transform: "translate3d(0, 0, 0)",
      }}
      className=' space-y-1'
    >
      <CopyOnHover
        copyValue={`Longitude: ${position.lng} Latitude: ${position.lat}`}
        className={cn("w-[40%]", copyLatClassName)}
      >
        <span className='text-sm '>
          Longitude: {position.lng} &nbsp; Latitude: {position.lat}
        </span>
      </CopyOnHover>
      <MapContainer
        center={latLngPosition} // Use the LatLng instance here
        zoom={14}
        scrollWheelZoom
        doubleClickZoom
        style={{
          height,
          width,
          backgroundColor: "var(--aldgray)",
        }}
        className='rounded-md overflow-hidden'
      >
        <TileLayer url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' />
        <Marker
          draggable={true}
          eventHandlers={eventHandlers}
          position={latLngPosition} // Use the LatLng instance here
          ref={markerRef}
          icon={mapMarker}
        ></Marker>
        <UpdateCenter center={latLngPosition} />
      </MapContainer>
    </div>
  );
};

export default MapPicker;
